import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Printer, Layers, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { trainingsplanerAPI } from '@/lib/api';
import TrainerZugaenge from '@/components/TrainerZugaenge';
import { UebungsbibliothekKarte } from '@/components/Uebungsbibliothek';
import MitgliederChallenges from '@/components/MitgliederChallenges';
import { KiPlanerCrm } from '@/components/KiPlaner';
import { FortschrittsfotosSchalter } from '@/components/Fortschrittsfotos';
import { TechnikVideosSchalter } from '@/components/TechnikVideos';
import { FreigeschaltetListe } from '@/components/TrainingslogTab';
import PlanPersonen from '@/components/PlanPersonen';
import PlanName from '@/components/PlanName';
import MitgliedSuche from '@/components/MitgliedSuche';
import { TrainingsplanEditor, PlanNeu, PersonenPlaene, Programme } from '@/components/PlanEditor2';
import { useAuth } from '@/contexts/AuthContext';
import api, { ernaehrungAPI } from '@/lib/api';

/* Trainingsplaner A5 (02.10.2026): Leseuebersicht aller laufenden 12-Wochen-Zyklen
   (Vorlage, Kurse, Ersteller, Woche, offene Punkte, Abdeckung) + Druckansicht A4 quer.
   Geplant wird in der Trainer-App (Teil B); hier lesen und drucken.
   Zwei Ausnahmen (10.10.2026): Bei Fitness-Zyklen weist die Leitung hier Personen zu
   („Für wen ist dieser Plan?“ — der eigene Plan für PT-Kunden, components/PlanPersonen.js), und
   jeder Zyklus lässt sich umbenennen (components/PlanName.js) — auch ein archivierter, den der
   Haken „auch archivierte“ in die Liste holt. */

const VORLAGE_LABEL = { fitness: 'Fitness', striking: 'Striking', grappling: 'Grappling', kids: 'Kids', minis: 'Minis', wettkampf: 'Wettkampf' };
const VORLAGE_REIHE = ['striking', 'grappling', 'kids', 'minis', 'wettkampf', 'fitness'];
const STATUS_CLS = { entwurf: 'bg-amber-500/10 text-amber-700 border-amber-200', freigegeben: 'bg-emerald-500/10 text-emerald-700 border-emerald-200',
                     archiviert: 'bg-muted text-muted-foreground border-border' };
const TYP_CLS = { deload: 'bg-sky-500/10 text-sky-700 border-sky-200', taper: 'bg-violet-500/10 text-violet-700 border-violet-200',
                  test: 'bg-orange-500/10 text-orange-700 border-orange-200', wettkampf: 'bg-red-500/10 text-red-700 border-red-200' };
const TYP_KURZ = { belastung: '', deload: 'E', taper: 'T', test: 'P', wettkampf: 'W' };

/* 'YYYY-MM-DD' lokal parsen — new Date('2026-10-05') waere UTC-Mitternacht und rutscht westlich von UTC einen Tag zurueck */
function lokal(iso) { const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); }
function datum(iso) { return iso ? lokal(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : ''; }

function Abdeckung({ a }) {
  if (!a) return <span className="text-muted-foreground">–</span>;
  if (a.prozent === null) return <span className="text-muted-foreground" title="Keine Prüfungsinhalte im Curriculum hinterlegt">keine Inhalte</span>;
  const cls = a.prozent >= 100 ? 'text-emerald-700' : a.prozent >= 75 ? 'text-amber-700' : 'text-red-700';
  return (
    <span className={`font-semibold tabular-nums ${cls}`} title={a.fehlend?.length ? `Fehlt: ${a.fehlend.join(', ')}` : 'Alle Inhalte geplant'}>
      {a.prozent} % <span className="font-normal text-muted-foreground">({a.abgedeckt}/{a.gesamt})</span>
    </span>
  );
}

function Wochenleiste({ wochen, aktuell }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="wochenleiste">
      {wochen.map((w) => (
        <div key={w.woche} title={`Woche ${w.woche} ab ${datum(w.montag)}${w.lehrplan_thema ? ` · ${w.lehrplan_thema}` : ''}`}
          className={`min-w-[52px] rounded border px-1.5 py-1 text-center text-[11px] ${w.woche === aktuell ? 'border-primary bg-primary/10 font-semibold' : 'border-border/60'} ${TYP_CLS[w.typ] || ''}`}>
          <div>W{w.woche}{TYP_KURZ[w.typ] ? <span className="ml-0.5 opacity-70">{TYP_KURZ[w.typ]}</span> : null}</div>
          <div className="text-[10px] text-muted-foreground truncate">{w.lehrplan_thema || datum(w.montag)}</div>
        </div>
      ))}
    </div>
  );
}

/* Eigener Plan (PT): Fitness-Zyklus ohne Kurs, der Personen zugewiesen ist. Er heißt meist nach dem
   Kunden — auf den Ausdruck für die Halle gehört er nicht. */
const istEigenerPlan = (z) => (z.vorlage || 'fitness') === 'fitness' && z.athleten > 0 && !(z.kurse || []).length;
const istArchiviert = (z) => z.status === 'archiviert';
/* Nicht auf den Ausdruck für die Halle: eigene Pläne und Archiviertes */
const ohneDruck = (z) => istEigenerPlan(z) || istArchiviert(z);

function Zeile({ z, offen, toggle, onGeaendert, onEditor }) {
  const fitness = (z.vorlage || 'fitness') === 'fitness';
  const nichtDrucken = ohneDruck(z) ? ' print:hidden' : '';
  return (
    <>
      <tr className={`border-t border-border/60 align-top hover:bg-accent/40 cursor-pointer print:cursor-auto${nichtDrucken}`} onClick={toggle} data-testid={`planer-zeile-${z.id}`}>
        <td className="py-2 pr-2 w-6 print:hidden">{offen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}</td>
        <td className="py-2 pr-3">
          <p className={`font-semibold${istArchiviert(z) ? ' text-muted-foreground' : ''}`}>{z.name}</p>
          <p className="text-xs text-muted-foreground">{z.disziplin}{z.zielgruppe && z.zielgruppe !== 'erwachsene' ? ` · ${z.zielgruppe}` : ''}{z.turnier ? ` · Turnier ${datum(z.turnier.datum)}${z.turnier.name ? ` (${z.turnier.name})` : ''}` : ''}{z.pruefung ? ` · Prüfung ${datum(z.pruefung.datum)}${z.pruefung.name ? ` (${z.pruefung.name})` : ''}` : ''}</p>
        </td>
        <td className="py-2 pr-3 text-sm">{z.kurse.length ? z.kurse.map((k) => <div key={k.id}>{k.name}{k.day ? <span className="text-muted-foreground"> · {k.day.slice(0, 2)} {k.start}</span> : null}</div>) : fitness && z.athleten ? <span>Eigener Plan (PT)</span> : <span className="text-muted-foreground">kein Kurs</span>}</td>
        <td className="py-2 pr-3 text-sm">{z.erstellt_von_name || '–'}</td>
        <td className="py-2 pr-3 text-sm whitespace-nowrap">
          {z.aktuelle_woche ? <><span className="font-semibold tabular-nums">Woche {z.aktuelle_woche}/{z.wochen_anzahl}</span><span className="text-muted-foreground"> · {z.phase}</span></>
            : <span className="text-muted-foreground">{lokal(z.startdatum) > new Date() ? `ab ${datum(z.startdatum)}` : `beendet ${datum(z.enddatum)}`}</span>}
          {z.typ_label && <Badge variant="outline" className={`ml-1 text-[10px] ${TYP_CLS[z.typ] || ''}`}>{z.typ_label}</Badge>}
          {z.lehrplan_thema && <p className="text-xs text-muted-foreground">{z.lehrplan_thema}</p>}
        </td>
        <td className="py-2 pr-3 text-sm tabular-nums">{z.offene_punkte ? <span className="text-amber-700 font-semibold" title={z.luecken.join('\n')}>{z.offene_punkte}</span> : <span className="text-emerald-700">0</span>}</td>
        <td className="py-2 pr-3 text-sm"><Abdeckung a={z.abdeckung} /></td>
        <td className="py-2 text-sm"><Badge variant="outline" className={`text-[10px] ${STATUS_CLS[z.status] || ''}`}>{z.status}</Badge>{z.athleten ? <p className="text-xs text-muted-foreground">{z.athleten} {z.vorlage === 'wettkampf' ? 'Kämpfer' : z.athleten === 1 ? 'Person' : 'Personen'}</p> : null}</td>
      </tr>
      {offen && (
        <tr className={`border-t border-border/30 bg-muted/20 print:bg-transparent${nichtDrucken}`}>
          <td className="print:hidden" />
          <td colSpan={7} className="py-2 pr-2">
            <Wochenleiste wochen={z.wochen} aktuell={z.aktuelle_woche} />
            {z.luecken?.length > 0 && <p className="text-xs text-amber-700 mt-2">Offen: {z.luecken.join(' · ')}{z.offene_punkte > z.luecken.length ? ' …' : ''}</p>}
            {z.abdeckung?.fehlend?.length > 0 && <p className="text-xs text-muted-foreground mt-1">Nicht im Zyklus: {z.abdeckung.fehlend.join(', ')}</p>}
            {/* Namen gehören nicht auf den Ausdruck für die Halle. empty:hidden: ohne Recht und ohne Kraftlog bleibt nichts übrig. */}
            <div className="mt-3 space-y-3 print:hidden empty:hidden">
              {onEditor && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => onEditor(z.id)} data-testid={`planer-editor-${z.id}`}><Pencil className="w-3.5 h-3.5" /> Im Editor öffnen</Button>}
              <PlanName z={z} onGeaendert={onGeaendert} />
              {/* Kraftlog gibt es nur zu Fitness-Zyklen */}
              {fitness && <PlanPersonen z={z} onGeaendert={onGeaendert} />}
              {/* key: nach einer Zuweisung (neue Version) lädt die Liste neu */}
              {fitness && <FreigeschaltetListe key={z.version} zid={z.id} ohneKurs={!z.kurse.length} />}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* W3.1 (29.10.2026): Trainingsplan-Editor, Pläne je Person, Programme — schreiben nur mit trainingsplaner.edit
   (der Server prüft dasselbe). Gleiche Bausteine wie auf /coach (components/PlanEditor2). */
const PLANER_BASIS = '/trainingsplaner';
const ernaehrungVorlagen = () => ernaehrungAPI.vorlagen();

function PersonBereich({ onOeffnen }) {
  const [person, setPerson] = useState(null);
  return (
    <Card className="print:hidden"><CardContent className="p-4 space-y-3">
      <p className="text-sm font-semibold">Pläne je Person — Standardplan, Auswahl in der App, Programm zuweisen</p>
      <MitgliedSuche wert={person} onWahl={setPerson} placeholder="Mitglied suchen…" />
      {person && <PersonenPlaene key={person.id} client={api} basis={PLANER_BASIS} leadId={person.id} onOeffnen={onOeffnen} />}
    </CardContent></Card>
  );
}

export default function PlanerPage() {
  const { isAdminPermissions, userPermissions } = useAuth();
  const darfEdit = isAdminPermissions || !!(userPermissions || {})['trainingsplaner.edit'];
  const [editor, setEditor] = useState(null);
  const [bereich, setBereich] = useState(null);       // null | 'person' | 'programme'
  const [zyklen, setZyklen] = useState(null);
  const [laden, setLaden] = useState(true);
  const [offen, setOffen] = useState(() => new Set());
  const [nurLaufend, setNurLaufend] = useState(true);
  // Archivierte lädt die Seite nur auf Wunsch — gebraucht werden sie hier allein zum Umbenennen
  const [mitArchiv, setMitArchiv] = useState(false);
  const lauf = useRef(0);

  const lade = useCallback(async () => {
    const nr = ++lauf.current;   // Haken schnell hin und her: nur die letzte Antwort zählt
    setLaden(true);
    try {
      const r = await trainingsplanerAPI.uebersicht(mitArchiv);
      if (nr === lauf.current) setZyklen(r.data);
    } catch (e) { if (nr === lauf.current) toast.error(e?.response?.data?.detail || 'Trainingsplaner nicht geladen'); }
    finally { if (nr === lauf.current) setLaden(false); }
  }, [mitArchiv]);
  useEffect(() => { lade(); }, [lade]);

  const gruppen = useMemo(() => {
    const liste = (zyklen || []).filter((z) => istArchiviert(z) || !nurLaufend || z.aktuelle_woche || lokal(z.startdatum) > new Date());
    const by = {};
    liste.forEach((z) => { (by[z.vorlage || 'fitness'] ||= []).push(z); });
    return VORLAGE_REIHE.filter((v) => by[v]).map((v) => ({ vorlage: v, zyklen: by[v] }));
  }, [zyklen, nurLaufend]);

  const toggle = (id) => setOffen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const drucken = () => { setOffen(new Set((zyklen || []).map((z) => z.id))); setTimeout(() => window.print(), 150); };
  const anzahl = (zyklen || []).length;

  if (editor) {
    return (
      <div className="space-y-4 fade-in" data-testid="planer-page">
        <TrainingsplanEditor client={api} basis={PLANER_BASIS} zid={editor} onGeoeffnet={setEditor}
          onZurueck={() => { setEditor(null); lade(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-in" data-testid="planer-page">
      {/* Druck A4 quer: App-Rahmen (Seitenleiste, Kopfzeile, Tabs) ausblenden, Inhalt an den linken Rand, Tabellen nicht abschneiden */}
      <style>{`@media print {
        @page { size: A4 landscape; margin: 12mm; }
        .print\\:hidden, aside, [data-print="rahmen"] { display: none !important; }
        main { margin-left: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important; }
        .overflow-x-auto { overflow: visible !important; }
      }`}</style>
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Layers className="w-6 h-6 text-primary" /> Trainingsplaner</h1>
          <p className="text-sm text-muted-foreground">Laufende Zyklen und Pläne — planen im Editor (Zyklus aufklappen › „Im Editor öffnen“ oder „Neuer Plan“), in der Trainer-App oder auf /coach; hier auch lesen und drucken. Bei Fitness-Zyklen weist du hier Personen zu (eigener Plan für PT-Kunden). Umbenennen: Zyklus aufklappen – das geht auch bei archivierten.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" className="accent-marke" checked={nurLaufend} onChange={(e) => setNurLaufend(e.target.checked)} /> nur laufende und geplante</label>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" className="accent-marke" checked={mitArchiv} onChange={(e) => setMitArchiv(e.target.checked)} data-testid="planer-mit-archiv" /> auch archivierte</label>
          <Button variant="outline" size="sm" onClick={lade} disabled={laden} className="gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${laden ? 'animate-spin' : ''}`} /> Aktualisieren</Button>
          <Button size="sm" onClick={drucken} className="gap-1.5" data-testid="planer-drucken"><Printer className="w-3.5 h-3.5" /> Drucken (A4 quer)</Button>
        </div>
      </div>
      {darfEdit && (
        <div className="flex flex-wrap items-start gap-2 print:hidden" data-testid="planer-editor-leiste">
          <PlanNeu client={api} basis={PLANER_BASIS} onAngelegt={setEditor} PersonFeld={MitgliedSuche} />
          <Button variant={bereich === 'person' ? 'default' : 'outline'} className="min-h-[44px]" onClick={() => setBereich((b) => (b === 'person' ? null : 'person'))}>Pläne je Person</Button>
          <Button variant={bereich === 'programme' ? 'default' : 'outline'} className="min-h-[44px]" onClick={() => setBereich((b) => (b === 'programme' ? null : 'programme'))}>Programme</Button>
        </div>
      )}
      {darfEdit && bereich === 'person' && <PersonBereich onOeffnen={setEditor} />}
      {darfEdit && bereich === 'programme' && <Card className="print:hidden"><CardContent className="p-4"><Programme client={api} basis={PLANER_BASIS} ernaehrungVorlagenLaden={ernaehrungVorlagen} /></CardContent></Card>}
      <TrainerZugaenge />
      <UebungsbibliothekKarte />
      <KiPlanerCrm onAngelegt={() => lade()} />
      <MitgliederChallenges />
      <FortschrittsfotosSchalter />
      <TechnikVideosSchalter />
      <div className="hidden print:block">
        <h1 className="text-xl font-bold">Trainingsplaner — Zyklusübersicht</h1>
        <p className="text-xs text-muted-foreground">Stand {new Date().toLocaleDateString('de-DE')} · E = Entlastung, T = Taper, P = Prüfung, W = Wettkampf</p>
      </div>

      {laden && !zyklen && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Lade Zyklen …</div>}
      {zyklen && !gruppen.length && (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">{anzahl ? 'Keine laufenden oder geplanten Zyklen — Haken oben entfernen für beendete.' : 'Noch kein Trainingszyklus angelegt. Trainer legen Zyklen in der App unter „Planer“ an.'}</CardContent></Card>
      )}
      {gruppen.map((g) => (
        <Card key={g.vorlage} className={`print:shadow-none print:border-0${g.zyklen.every(ohneDruck) ? ' print:hidden' : ''}`} data-testid={`planer-gruppe-${g.vorlage}`}>
          <CardContent className="p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">{VORLAGE_LABEL[g.vorlage] || g.vorlage} <span className="font-normal">· {g.zyklen.length}</span></h2>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="print:hidden" />
                    <th className="pb-1 pr-3 font-medium">Zyklus</th>
                    <th className="pb-1 pr-3 font-medium">Kurse</th>
                    <th className="pb-1 pr-3 font-medium">Ersteller</th>
                    <th className="pb-1 pr-3 font-medium">Woche</th>
                    <th className="pb-1 pr-3 font-medium">Offen</th>
                    <th className="pb-1 pr-3 font-medium">Abdeckung</th>
                    <th className="pb-1 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {g.zyklen.map((z) => <Zeile key={z.id} z={z} offen={offen.has(z.id)} toggle={() => toggle(z.id)} onGeaendert={lade} onEditor={darfEdit ? setEditor : undefined} />)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
